/**
 * Utility functions for transaction-related operations
 * Extracted from Activity.tsx to improve code organization and IDE performance
 */

/**
 * Format a Date object to YYYY-MM-DD string format
 */
export const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Format a date string (YYYY-MM-DD) for display in date fields.
 * @param dateString - YYYY-MM-DD
 * @param dateFormat - When "DD/MM/YYYY", display day first; otherwise MM/DD/YYYY (month first)
 */
export const formatDateForDisplay = (
  dateString: string,
  dateFormat?: "MM/DD/YYYY" | "DD/MM/YYYY"
): string => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  if (dateFormat === "DD/MM/YYYY") {
    return `${day}/${month}/${year}`;
  }
  return `${month}/${day}/${year}`;
};

/**
 * Get calendar days for a given year and month
 * Returns an array of Date objects including days from previous/next month to fill the grid
 */
export const getCalendarDays = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  
  const days: Date[] = [];
  
  // Add days from previous month
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
  
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push(new Date(prevYear, prevMonth, prevMonthLastDay - i));
  }
  
  // Add all days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }
  
  // Add days from next month to fill the grid (always show 6 weeks = 42 days)
  const totalDays = days.length;
  const remainingDays = 42 - totalDays; // 6 weeks * 7 days = 42
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  
  for (let day = 1; day <= remainingDays; day++) {
    days.push(new Date(nextYear, nextMonth, day));
  }
  
  return days;
};

/**
 * Format currency amount with proper decimal handling
 * Rounds to 4 decimal places internally (for DB precision) but displays based on decimalPlaces parameter
 * @param amount - Amount to format
 * @param currency - Currency code (e.g., "USD")
 * @param decimalPlaces - Optional number of decimal places to display (0-4). If not provided, uses max 4 decimals.
 */
export const formatCurrency = (amount: number, currency: string = "USD", decimalPlaces?: number): string => {
  // Round to 4 decimal places internally (keep full precision in calculations)
  const rounded = Math.round(amount * 10000) / 10000;
  
  // If decimalPlaces is specified, use it for both min and max
  if (decimalPlaces !== undefined) {
    // Format with exact decimal places
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(rounded);
    return formatted;
  }
  
  // Default behavior: use formatToParts to remove trailing zeros (max 4 decimals)
  const parts = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).formatToParts(rounded);
  
  // Find the fraction part and check if it has significant digits
  const fractionPart = parts.find(p => p.type === "fraction");
  const hasSignificantFraction = fractionPart && fractionPart.value.replace(/0+$/, "") !== "";
  
  let result = "";
  for (const part of parts) {
    if (part.type === "minusSign") {
      // Include minus sign for negative numbers
      result += part.value;
    } else if (part.type === "currency") {
      result += part.value;
    } else if (part.type === "integer" || part.type === "group") {
      result += part.value;
    } else if (part.type === "decimal") {
      // Only add decimal separator if there are significant fraction digits
      if (hasSignificantFraction) {
        result += part.value;
      }
    } else if (part.type === "fraction") {
      // Remove trailing zeros from fraction part
      const fraction = part.value.replace(/0+$/, "");
      if (fraction !== "") {
        result += fraction;
      }
    }
  }
  return result;
};

/**
 * Format amount as number only (no currency symbol/code). Use when space is limited (e.g. summary cards).
 * @param amount - Amount to format
 * @param decimalPlaces - Number of decimal places (0-4)
 */
export const formatAmountOnly = (amount: number, decimalPlaces: number = 2): string => {
  const rounded = Math.round(amount * 10000) / 10000;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(rounded);
};

/**
 * Format exchange rate without trailing zeros, max 5 decimal places
 */
export const formatRate = (rate: number | string): string => {
  const numRate = typeof rate === "string" ? parseFloat(rate) : rate;
  if (isNaN(numRate)) return "0";
  // Round to 5 decimal places and remove trailing zeros
  const rounded = Math.round(numRate * 100000) / 100000;
  // If it's a whole number, return without decimals
  if (rounded % 1 === 0) {
    return rounded.toString();
  }
  // Format with up to 5 decimal places and remove trailing zeros
  return rounded.toFixed(5).replace(/\.?0+$/, "");
};

/**
 * Format time to HH:mm in local time
 */
export const formatTimeLocal = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Get current time in HH:mm format
 */
export const getCurrentTime = (): string => {
  return formatTimeLocal(new Date());
};

/**
 * Convert 24-hour time (HH:mm) to 12-hour format (h:mm AM/PM)
 */
export const convertTo12Hour = (time24: string): { hour: number; minute: string; period: "AM" | "PM" } => {
  const [hour24, minute] = time24.split(":");
  const hour = parseInt(hour24, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return { hour: hour12, minute, period };
};

/**
 * Convert 12-hour format (hour, minute, period) to 24-hour time (HH:mm)
 */
export const convertTo24Hour = (hour12: number, minute: string, period: "AM" | "PM"): string => {
  let hour24 = hour12;
  if (period === "PM" && hour12 !== 12) {
    hour24 = hour12 + 12;
  } else if (period === "AM" && hour12 === 12) {
    hour24 = 0;
  }
  return `${String(hour24).padStart(2, "0")}:${minute}`;
};

/**
 * Combine date (YYYY-MM-DD) and time (HH:mm) into ISO string for API
 * If time is empty, use midnight (00:00:00) to indicate time was not set
 */
export const combineDateAndTime = (date: string, time: string): string => {
  const timeToUse = time || "00:00";
  const dateTime = new Date(`${date}T${timeToUse}`);
  return dateTime.toISOString();
};

/**
 * Returns true if the search query matches the transaction ID pattern (e.g. DV-1, DEV-42).
 * Used to detect when user is searching by ID so we can auto-switch period to "all time".
 * Case-insensitive to match filter behavior.
 */
export function isSearchByTransactionId(
  searchQuery: string,
  transactionIdPrefix: string | undefined
): boolean {
  if (!searchQuery || !transactionIdPrefix || transactionIdPrefix === "") return false;
  const trimmed = searchQuery.trim();
  if (!trimmed) return false;
  const escaped = transactionIdPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`^${escaped}-\\d+$`, "i");
  return pattern.test(trimmed);
}

/**
 * User-friendly transaction ID (e.g. DV-1, DEV-2).
 * Returns undefined when prefix or sequence is missing (feature off or not yet assigned).
 */
export function getTransactionFriendlyId(
  prefix: string | undefined,
  sequence: number | undefined
): string | undefined {
  if (prefix == null || prefix === "" || sequence == null) return undefined;
  return `${prefix}-${sequence}`;
}

/** When ID is long (e.g. Firestore doc id), show prefix + "..." in data field; full ID still used for copy/tooltip. */
export function truncateIdForDisplay(id: string | undefined | null, maxLength: number = 14): string {
  if (id == null || id === "") return "";
  if (id.length <= maxLength) return id;
  return id.slice(0, maxLength - 3) + "...";
}

/**
 * Copy text to clipboard. Works in secure (HTTPS) and insecure (e.g. mobile dev) contexts.
 * Uses navigator.clipboard when available, otherwise falls back to execCommand("copy").
 * Returns true if copy succeeded, false otherwise.
 */
export function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator?.clipboard?.writeText === "function") {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return Promise.resolve(ok);
  } catch {
    return Promise.resolve(false);
  }
}
